import React, { useState } from 'react';
import { useRiskResults } from '@/features/health/hooks/useRiskResults';
import ScrollToTopButton from "@/widgets/ButtonTop/ButtonTop";
import { useHealthResults } from '@/features/health/hooks/useHealthResults';

export const HealthRecommendationsPage: React.FC = () => {
    const { data: risks, loading } = useRiskResults();
    const health = useHealthResults();
    const isMobile = window.innerWidth <= 768;

    const levelOrder = ['very_low', 'below_normal', 'normal', 'above_average', 'high'];
    const [expandedRiskIds, setExpandedRiskIds] = useState<string[]>([]);
    const [showDetails, setShowDetails] = useState(false);

    const toggleDetails = () => setShowDetails(prev => !prev);

    const toggleRiskDescription = (id: string) => {
        setExpandedRiskIds(prev =>
            prev.includes(id) ? prev.filter(riskId => riskId !== id) : [...prev, id]
        );
    };

    const getLevelClass = (level: string) => {
        const map: Record<string, string> = {
            very_low: 'very-low',
            below_normal: 'low',
            normal: 'medium',
            above_average: 'high',
            high: 'very-high',
        };
        return map[level] || '';
    };

    const getTagClass = (level: string) => {
        switch (level) {
            case 'high': return 'risk-card__tag risk-card__tag_high';
            case 'middle':
            case 'medium': return 'risk-card__tag risk-card__tag_middle';
            case 'low': return 'risk-card__tag risk-card__tag_low';
            default: return '';
        }
    };

    return (
        <>
        <main className="layout__content">
            {/* Вступление */}
            <section className="section layout__section test-results">
                <div className="container">
                    <div className="section__content">
                        <div className="test-results__intro test-results-intro">
                            <div className="test-results-intro__inner">
                                <div className="test-results-intro__left">
                                    <h1 className="title test-results__title"></h1>
                                    <span className="test-results__health-level test-results-health-level">
                                        Твой уровень здоровья:
                                        <span className="test-results-health-level__value" style={{ color: health?.iconFill }}>
                                            {health?.healthStatus}
                                        </span>
                                    </span>
                                    <p className="test-results__description">{health?.descTest}</p>
                                </div>
                                <div className="test-results-intro__right">
                                    {health && (
                                        <div className="health-emotion test-results__health-emotion">
                                            <svg className="svg-icon" style={{ fill: health.iconFill }}>
                                                <use href={`/img/sprite-health-emotions.svg#${health.icon}`} />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Шкала */}
                        <div className="health-bar test-results__health-bar">
                            <div className="health-bar__levels">
                                {levelOrder.map(level => {
                                    const cssClass = getLevelClass(level);
                                    const isCurrent = cssClass === health?.color;
                                    return (
                                        <div
                                            key={level}
                                            className={`health-bar__level health-bar-level health-bar-level_${cssClass} ${isCurrent ? 'health-bar-level_current' : ''}`}
                                        >
                                            <div className="health-bar-level__line"></div>
                                            <span className="health-bar-level__label">
                                                {{
                                                    high: 'Высокий',
                                                    above_average: 'Выше среднего',
                                                    normal: 'Нормальный',
                                                    below_normal: 'Ниже нормы',
                                                    very_low: 'Очень низкий',
                                                }[level]}
                                            </span>
                                            {isCurrent && (
                                                <div className="marker health-bar-level__marker">
                                                    <span className="marker__label">Вы сейчас здесь</span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Риски */}
            <section className="section layout__section risks">
                <div className="container">
                    <header className="section__header risks__header">
                        <h2 className="title section__title risks__title risks-title">
                            <span className="risks-title__indicator">!</span>
                            Твои риски
                        </h2>
                        <p className="risks-intro__description">
                            Мы проанализировали ваши ответы и сделали вывод, какие риски конкретных заболеваний у вашего организма наиболее высокие.
                        </p>
                    </header>
                    <div className="section__content">
                        <div className="risks-cards__grid">
                            {loading ? (
                                <p>Загрузка...</p>
                            ) : (
                                risks.map(risk => (
                                    <div className="risks-cards__col" key={risk.id}>
                                        <div
                                            className={`risk-card-mobile-wrapper ${expandedRiskIds.includes(risk.id) ? 'expanded' : ''}`}
                                            onClick={() => toggleRiskDescription(risk.id)}
                                        >
                                        <div className='riskMob'>
                                                <div className="risk-card risk-card_flex">

                                                    <div className="risk-card__left">
                                                        <img className="svg-icon risk-card__icon" src={risk.icon} />
                                                        <h6 className="risk-card__title">{risk.title}</h6>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <span style={{ color: '#7E7E7E', fontWeight: '400' }}>Риск</span>
                                                        <div className={getTagClass(risk.level)}>
                                                            {risk.level === 'high' ? 'Высокий' : risk.level === 'middle' ? 'Средний' : 'Низкий'}
                                                        </div>
                                                    </div>

                                                </div>
                                            {isMobile && expandedRiskIds.includes(risk.id) && (
                                                <div className="risk-card__description-mobile">
                                                    <p>{risk.description}</p>
                                                </div>
                                            )}

                                        </div>


                                            {/* Внутреннее описание (мобилка) */}

                                        </div>
                                    </div>

                                ))
                            )}
                        </div>

                        {/* Детали для десктопа */}
                        <div className="risks-intro__more risks-intro__more_new risks-intro__more_desktop">
                            <div className="button button_link" onClick={toggleDetails}>
                                <span className="button__label">{showDetails ? 'Свернуть' : 'Подробнее'}</span>
                                <svg className="svg-icon button__icon">
                                    <use href={`/img/sprite-icons.svg#arrow-${showDetails ? 'up' : 'down'}`} />
                                </svg>
                            </div>
                        </div>

                        {showDetails && (
                            <div className="risks__details risks-details _visible">
                                {risks.map(risk => (
                                    <div className="risks-details__item risks-details-item" key={risk.id}>
                                        <div className="risks-details-item__inner">
                                            <div className="risks-details-item__left">
                                                <div className="risk-card-more risk-card-more_flex">
                                                    <img className="svg-icon risk-card__icon" src={risk.icon} />
                                                    Риск{' '}
                                                    <div className={getTagClass(risk.level)}>
                                                        {risk.level === 'high'
                                                            ? 'Высокий'
                                                            : risk.level === 'middle'
                                                                ? 'Средний'
                                                                : 'Низкий'}
                                                    </div>
                                                </div>
                                                <h6 className="risks-details-item__title">{risk.title}</h6>
                                            </div>
                                            <div className="risks-details-item__right">
                                                <div className="text risks-details-item__text">
                                                    <p>{risk.description}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>
                <section className="section layout__section recommendations">
                    <div className="container">
                        <div className="section__content">
                            <div className="recommendations__inner">
                                <div className="recommendations__left">
                                    <div className="recommendations__element recommendations-element">
                                        <div className="recommendations-element__inner">
                                            <div className="recSVG">
                                                <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <mask id="mask0_9579_67866"  maskUnits="userSpaceOnUse" x="0" y="0" width="50" height="50">
                                                        <path d="M49.9609 49.9609V0.0390625H0.0390625V49.9609H49.9609Z" fill="white" stroke="white" stroke-width="0.0787974"/>
                                                    </mask>
                                                    <g mask="url(#mask0_9579_67866)">
                                                        <path d="M0.732422 6.99417L7.73057 8.32395C8.26709 8.42591 8.82227 8.33762 9.30078 8.07454L21.0658 1.60374C22.1061 1.03157 23.274 0.731569 24.4611 0.731569H34.2321C34.7538 0.731569 35.1767 1.15452 35.1767 1.6761C35.1767 2.90833 34.2245 3.93089 32.9956 4.01868L24.6298 4.61624C24.3594 4.63557 24.0985 4.72483 23.873 4.87522L21.8688 6.21135" stroke="#E2EF4A" stroke-width="1.18196" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                                        <path d="M14.8233 10.9108L23.0099 9.02165C24.028 8.78669 25 9.55993 25 10.6049C25 11.2654 24.6064 11.8624 23.9993 12.1225L17.3075 14.9904C15.1671 15.9078 12.8232 16.2473 10.5104 15.9753L0.732422 14.825" stroke="#E2EF4A" stroke-width="1.18196" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                                        <path d="M49.2685 43.005L42.2703 41.6752C41.7338 41.5732 41.1786 41.6615 40.7001 41.9246L28.9351 48.3954C27.8948 48.9676 26.7269 49.2676 25.5397 49.2676H15.7688C15.2471 49.2676 14.8242 48.8446 14.8242 48.323C14.8242 47.0908 15.7764 46.0683 17.0053 45.9805L25.3711 45.3829C25.6415 45.3636 25.9023 45.2743 26.1279 45.1239L28.1321 43.7878" stroke="#E2EF4A" stroke-width="1.18196" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                                        <path d="M35.1767 39.0939L26.9901 40.9831C25.972 41.218 25 40.4448 25 39.3999C25 38.7393 25.3936 38.1424 26.0007 37.8822L32.6925 35.0143C34.8329 34.0969 37.1768 33.7574 39.4896 34.0295L49.2676 35.1798" stroke="#E2EF4A" stroke-width="1.18196" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                                        <path d="M25.2984 14.9393C25.9785 15.5685 26.5161 16.3323 26.8803 17.1748C28.545 16.0402 30.7127 15.6725 32.7906 16.3982C36.4681 17.6824 38.0406 21.9617 36.5649 25.5666C33.6633 32.6551 24.0909 34.7493 22.0765 35.1162C21.8368 35.1599 21.597 35.0896 21.4175 34.925C19.9283 33.5602 13.1451 26.8923 14.1402 19.5231L14.2123 19.1559" stroke="#E2EF4A" stroke-width="1.18196" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/>
                                                    </g>
                                                </svg>
                                            </div>
                                            <span
                                                className="recommendations-element__label">Как улучшить свое здоровье?</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="recommendations__right">
                                    <div className="recommendations__info">
                                        <h2 className="title recommendations__title">Рекомендации</h2>
                                        <p className="recommendations__description">
                                            Мы - специалисты LAB4U, медицинской онлайн-лаборатории.</p>
                                        <div className="text recommendations__text">
                                            <p>
                                                Даем возможность людям управлять здоровьем в современном темпе жизни:
                                                запись и
                                                оплата на сайте, сдача биоматериала в широкой сети партнерских центров и
                                                максимальная автоматизация обработки ваших анализов. Профилактика с
                                                LAB4U - это
                                                выгодно и удобно. На основе нашего 10-летнего опыта был разработан тест,
                                                который
                                                поможет выявить риски заболеваний.
                                                Сервис проанализирует ваши возможные риски и сформирует 3 блока
                                                рекомендаций:
                                                программу тренировок, здоровое меню и список необходимых микроэлементов
                                                для
                                                достижения ваших здоровых целей.
                                            </p><h5>С помощью генеративной нейросети мы подготовим план рекомендаций на
                                            4 недели с
                                            трекингом привычек на каждый день. Вы будете ежедневно отмечать свои успехи
                                            и
                                            следить за прогрессом.</h5>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <section className="section layout__section neural-network">
                    <div className="container">
                        <header className="section__header section__header_center">
                            <h2 className="title section__title">Нейросеть здорового образа жизни</h2>
                        </header>
                        <div className="section__content">
                            <div className="neural-network__intro neural-network-intro">
                                <div className="neural-network-intro__inner">
                                    <div className="neural-network-intro__left">
                                        <div className="text neural-network-intro__text">
                                            <h5>Онлайн дневник с результатами, персональными рекомендациями, заданиями и
                                                аналитикой поможет тебе вести здоровый образ жизни.</h5>
                                            <p>
                                                Вы сможете снизить риски заболеваний, если будете следовать
                                                разработанным
                                                индивидуально для вас программам тренировок и питания. Нейросеть будет
                                                запоминать ваши результаты и с каждым месяцем лучше адаптировать
                                                программы для
                                                максимальной эффективности. </p>
                                        </div>
                                    </div>
                                    <div className="neural-network-intro__right">
                                        <div className="neural-network-intro__image-wrapper">
                                            <img className="neural-network-intro__image"
                                                 src="/img/test-result-img.png"
                                                 alt=""/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="neural-network__cards neural-network-cards">
                                <div className="neural-network-cards__grid">
                                    <div className="neural-network-cards__col">
                                        <div className="neural-network-card">
                                            <div className="neural-network-card__image-wrapper">
                                                <img className="neural-network-card__image"
                                                     src="/img/neural-network-card-image1.png"
                                                     alt="Индивидуальная программа физических упражнений"/>
                                            </div>
                                            <h3 className="neural-network-card__title">Индивидуальная программа
                                                физических
                                                упражнений</h3>
                                        </div>
                                    </div>
                                    <div className="neural-network-cards__col">
                                        <div className="neural-network-card">
                                            <div className="neural-network-card__image-wrapper">
                                                <img className="neural-network-card__image"
                                                     src="/img/neural-network-card-image2.png"
                                                     alt="Подобранная диета с учетом пройденного теста"/>
                                            </div>
                                            <h3 className="neural-network-card__title">Подобранная диета с учетом
                                                пройденного
                                                теста</h3>
                                        </div>
                                    </div>
                                    <div className="neural-network-cards__col">
                                        <div className="neural-network-card">
                                            <div className="neural-network-card__image-wrapper">
                                                <img className="neural-network-card__image"
                                                     src="/img/neural-network-card-image3.png"
                                                     alt="Персональная подборка витаминов и минералов от проверенных брендов"/>
                                            </div>
                                            <h3 className="neural-network-card__title">Персональная подборка витаминов и
                                                минералов
                                                от проверенных брендов</h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </section>
                <ScrollToTopButton />
            </main>
        </>
    );
};
